import React, { useEffect, useMemo, useState } from 'react';
import {
  DownloadSimple,
  Eye,
  FileText,
  SpinnerGap,
  Trash,
  UploadSimple
} from '@phosphor-icons/react';
import ActionMenu from '../../../../shared/components/ui/ActionMenu.jsx';
import Alerts from '../../../../shared/components/ui/Alerts.jsx';
import DocumentPreviewModal from '../DocumentPreviewModal.jsx';
import { useGuidedTourStep } from '../../../../shared/hooks/useGuidedTour.js';
import {
  deleteTaskDocument,
  downloadTaskDocument,
  fetchTaskDocumentBlob,
  fetchTaskDocuments,
  updateTaskDocument,
  uploadTaskDocument
} from '../../../../services/tasks/taskDocumentsService.js';

const INITIAL_FORM_STATE = {
  name: '',
  file: null,
  visibleToTechnician: false
};


const formatDate = (value) => {
  if (!value) {
    return 'Sin fecha';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'Sin fecha';
  }

  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(parsedDate);
};

const formatFileSize = (size) => {
  if (!Number.isFinite(size) || size <= 0) {
    return '';
  }

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  if (size < 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const getDocumentTypeLabel = (document) => {
  if (document.extension) {
    return document.extension;
  }

  const mimeType = String(document.mimeType || '').toLowerCase();
  if (mimeType.startsWith('image/')) {
    return 'Imagen';
  }

  if (mimeType.includes('pdf')) {
    return 'PDF';
  }

  return 'Archivo';
};

const isPdfDocument = (document) => {
  const extension = String(document?.extension || '').toUpperCase();
  const mimeType = String(document?.mimeType || '').toLowerCase();

  return extension === 'PDF' || mimeType.includes('pdf');
};

function DocumentsTab({ taskId }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Recorrido guiado (taskDetailTour): el paso de carga (openUpload) muestra el formulario.
  useGuidedTourStep((step) => setUploadOpen(Boolean(step?.openUpload)));
  const [uploading, setUploading] = useState(false);
  const [downloadId, setDownloadId] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  const [updatingVisibilityId, setUpdatingVisibilityId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [documentPendingDelete, setDocumentPendingDelete] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [formState, setFormState] = useState(INITIAL_FORM_STATE);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    if (!feedback.message) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setFeedback({ type: '', message: '' });
    }, 5000);

    return () => window.clearTimeout(timerId);
  }, [feedback.message]);

  useEffect(() => {
    if (!taskId) {
      setDocuments([]);
      setLoading(false);
      setError('No se encontró el identificador de la tarea.');
      return;
    }

    let cancelled = false;

    const loadDocuments = async () => {
      try {
        setLoading(true);
        setError(null);

        const fetchedDocuments = await fetchTaskDocuments(taskId);
        if (cancelled) {
          return;
        }

        setDocuments(fetchedDocuments);
      } catch {
        if (cancelled) {
          return;
        }

        setError('No se pudieron cargar los documentos de la tarea.');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDocuments();

    return () => {
      cancelled = true;
    };
  }, [taskId, reloadKey]);

  const orderedDocuments = useMemo(() => {
    return [...documents].sort((left, right) => {
      const leftDate = new Date(left?.createdAt || 0).getTime();
      const rightDate = new Date(right?.createdAt || 0).getTime();
      return rightDate - leftDate;
    });
  }, [documents]);

  const handleFormValueChange = (field, value) => {
    setFeedback({ type: '', message: '' });
    setFormState((current) => ({
      ...current,
      [field]: value
    }));
  };

  const handleFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    setFeedback({ type: '', message: '' });

    setFormState((current) => ({
      ...current,
      file: nextFile,
      name: current.name.trim() ? current.name : (nextFile?.name || '')
    }));
  };

  const resetForm = () => {
    setFormState(INITIAL_FORM_STATE);
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (!taskId) {
      setFeedback({
        type: 'error',
        message: 'No se encontró la tarea para asociar el documento.'
      });
      return;
    }

    if (!formState.file) {
      setFeedback({
        type: 'error',
        message: 'Selecciona un archivo para subir.'
      });
      return;
    }

    setUploading(true);
    setFeedback({ type: '', message: '' });

    try {
      await uploadTaskDocument(taskId, formState);
      resetForm();
      setUploadOpen(false);
      setReloadKey((current) => current + 1);
      setFeedback({
        type: 'success',
        message: 'Documento subido correctamente.'
      });
    } catch (uploadError) {
      setFeedback({
        type: 'error',
        message: (uploadError.message || 'No se pudo subir el documento.')
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (document) => {
    setDownloadId(document.id);
    setFeedback({ type: '', message: '' });

    try {
      const { blob, fileName } = await downloadTaskDocument(taskId, document);
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');

      link.href = url;
      link.download = fileName;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      setFeedback({
        type: 'error',
        message: downloadError.message || 'No se pudo descargar el documento.'
      });
    } finally {
      setDownloadId(null);
    }
  };

  const handleVisibilityToggle = async (document, nextVisibleToTechnician) => {
    setUpdatingVisibilityId(document.id);
    setFeedback({ type: '', message: '' });

    try {
      const updatedDocument = await updateTaskDocument(taskId, document, {
        visibleToTechnician: nextVisibleToTechnician
      });

      setDocuments((current) => current.map((item) => (
        item.id === document.id
          ? {
            ...item,
            ...updatedDocument
          }
          : item
      )));

      setFeedback({
        type: 'success',
        message: nextVisibleToTechnician
          ? 'El documento ahora es visible para el técnico.'
          : 'El documento dejó de estar visible para el técnico.'
      });
    } catch (updateError) {
      setFeedback({
        type: 'error',
        message: updateError.message || 'No se pudo actualizar la visibilidad del documento.'
      });
    } finally {
      setUpdatingVisibilityId(null);
    }
  };

  const handleDeleteDocument = (document) => {
    setDocumentPendingDelete(document);
  };

  const handleCloseDeleteAlert = () => {
    if (deletingId) {
      return;
    }

    setDocumentPendingDelete(null);
  };

  const handleConfirmDeleteDocument = async () => {
    if (!documentPendingDelete) {
      return;
    }

    const documentToDelete = documentPendingDelete;

    setDeletingId(documentToDelete.id);
    setFeedback({ type: '', message: '' });

    try {
      await deleteTaskDocument(taskId, documentToDelete);
      setDocuments((current) => current.filter((item) => item.id !== documentToDelete.id));
      setDocumentPendingDelete(null);
      setFeedback({
        type: 'success',
        message: 'Documento eliminado correctamente.'
      });
    } catch (deleteError) {
      setDocumentPendingDelete(null);
      setFeedback({
        type: 'error',
        message: deleteError.message || 'No se pudo eliminar el documento.'
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleOpenPreview = async (document) => {
    if (isPdfDocument(document)) {
      setPreviewingId(document.id);
      setFeedback({ type: '', message: '' });

      try {
        const { blob } = await fetchTaskDocumentBlob(taskId, document);
        const pdfUrl = window.URL.createObjectURL(blob);
        window.open(pdfUrl, '_blank', 'noopener,noreferrer');

        window.setTimeout(() => {
          window.URL.revokeObjectURL(pdfUrl);
        }, 60000);
      } catch (previewError) {
        setFeedback({
          type: 'error',
          message: previewError.message || 'No se pudo abrir el PDF.'
        });
      } finally {
        setPreviewingId(null);
      }

      return;
    }

    setPreviewDocument(document);
  };

  const handleClosePreview = () => {
    setPreviewDocument(null);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((item) => (
          <div key={item} className="animate-pulse rounded-2xl border border-line bg-bg/70 p-5">
            <div className="mb-3 h-5 w-44 rounded bg-surface-2" />
            <div className="h-4 w-24 rounded bg-surface-2" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-3xl border border-red-800/70 bg-red-950/20 p-8 text-center">
        <p className="text-lg font-semibold text-red-200">{error}</p>
        <button
          type="button"
          onClick={() => setReloadKey((current) => current + 1)}
          className="mt-4 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-content transition-colors hover:bg-red-500"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-line bg-surface/70 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-content">Documentos Adjuntos</h3>
            <p className="mt-1 text-sm text-content-muted">
              Visualice documentos de la tarea, descárguelos o cargue nuevos archivos.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-line bg-bg/70 px-3 py-1 text-xs font-semibold text-content">
              {orderedDocuments.length} documento{orderedDocuments.length === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              onClick={() => {
                setFeedback({ type: '', message: '' });
                setUploadOpen((current) => !current);
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-amber px-4 py-2 text-sm font-semibold text-on-amber transition-colors hover:bg-amber-hover"
            >
              <UploadSimple size={16} />
              {uploadOpen ? 'Cancelar' : 'Subir documento'}
            </button>
          </div>
        </div>

        {uploadOpen && (
          <form data-tour="task-doc-upload" onSubmit={handleUpload} className="mt-6 rounded-2xl border border-line bg-bg/60 p-5">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
              <div className="space-y-4">
                <div>
                  <label htmlFor="document-name" className="mb-2 block text-sm font-medium text-content">
                    Nombre del documento
                  </label>
                  <input
                    id="document-name"
                    type="text"
                    value={formState.name}
                    onChange={(event) => handleFormValueChange('name', event.target.value)}
                    placeholder="Ej: Orden de trabajo"
                    className="w-full rounded-xl border border-line bg-bg/80 px-3 py-2 text-sm text-content outline-none transition-colors focus:border-amber"
                  />
                </div>

                <div>
                  <label htmlFor="document-file" className="mb-2 block text-sm font-medium text-content">
                    Archivo <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="document-file"
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="document-file"
                    className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-line bg-bg/80 px-3 py-3 text-sm text-content transition-colors hover:border-amber"
                  >
                    <span className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-amber px-3 py-2 text-sm font-semibold text-on-amber transition-colors hover:bg-amber-hover">
                      <UploadSimple size={16} />
                      Seleccionar archivo
                    </span>
                    <span className="truncate">
                      {formState.file?.name || 'Ningún archivo seleccionado'}
                    </span>
                  </label>
                </div>

                <label className="flex items-center justify-between rounded-2xl border border-line bg-surface/80 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-content">Visible para el técnico</p>
                    <p className="text-xs text-content-muted">
                      Estado: {formState.visibleToTechnician ? 'Sí' : 'No'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formState.visibleToTechnician}
                    onChange={(event) => handleFormValueChange('visibleToTechnician', event.target.checked)}
                    className="h-5 w-5 cursor-pointer rounded border-line bg-surface text-amber focus:ring-amber"
                  />
                </label>
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={uploading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber px-5 py-3 text-sm font-semibold text-on-amber transition-colors hover:bg-amber-hover disabled:cursor-not-allowed disabled:opacity-70 lg:w-auto"
                >
                  {uploading ? <SpinnerGap size={16} className="animate-spin" /> : <UploadSimple size={16} />}
                  {uploading ? 'Subiendo...' : 'Guardar documento'}
                </button>
              </div>
            </div>
          </form>
        )}

        {feedback.message && (
          <div
            className={`mt-4 rounded-xl border px-4 py-3 text-sm ${
              feedback.type === 'success'
                ? 'border-green-600 bg-green-900/20 text-green-300'
                : 'border-red-600 bg-red-900/20 text-red-300'
            }`}
          >
            {feedback.message}
          </div>
        )}
      </div>

      {orderedDocuments.length === 0 ? (
        <div className="rounded-3xl border border-line bg-surface/70 p-8 text-center text-content">
          No hay documentos asociados a esta tarea.
        </div>
      ) : (
        <div className="space-y-3">
          {orderedDocuments.map((document) => {
            const isDownloading = downloadId === document.id;
            const isPreviewing = previewingId === document.id;
            const isUpdatingVisibility = updatingVisibilityId === document.id;
            const isDeleting = deletingId === document.id;
            const isActionLoading = isDownloading || isPreviewing;
            const isBusy = isActionLoading || isUpdatingVisibility || isDeleting;
            const actionOptions = [
              {
                label: isPreviewing ? 'Abriendo PDF...' : 'Previsualizar',
                icon: isPreviewing ? <SpinnerGap size={16} className="animate-spin" /> : <Eye size={16} />,
                onClick: () => handleOpenPreview(document),
                disabled: isBusy
              },
              {
                label: isDownloading ? 'Descargando...' : 'Descargar',
                icon: isDownloading ? <SpinnerGap size={16} className="animate-spin" /> : <DownloadSimple size={16} />,
                onClick: () => handleDownload(document),
                disabled: isBusy
              },
              {
                label: isDeleting ? 'Eliminando...' : 'Eliminar',
                icon: isDeleting ? <SpinnerGap size={16} className="animate-spin" /> : <Trash size={16} />,
                onClick: () => handleDeleteDocument(document),
                disabled: isBusy
              }
            ];

            return (
              <div
                key={document.id}
                className="flex flex-col gap-4 rounded-2xl border border-line bg-bg/70 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-start gap-3">
                  <div className="rounded-xl bg-surface-2 p-3 text-content-muted">
                    <FileText size={20} />
                  </div>

                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-content sm:text-base">{document.name}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-content-muted sm:text-sm">
                      <span>{getDocumentTypeLabel(document)}</span>
                      <span className="text-content-muted">•</span>
                      <span>{formatDate(document.createdAt)}</span>
                      {formatFileSize(document.size) ? (
                        <>
                          <span className="text-content-muted">•</span>
                          <span>{formatFileSize(document.size)}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <label
                    className={`inline-flex items-center gap-3 rounded-2xl border px-4 py-2 text-sm font-semibold transition-colors ${
                      document.visibleToTechnician
                        ? 'border-emerald-700/70 bg-emerald-900/20 text-emerald-300'
                        : 'border-line bg-surface text-content'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={document.visibleToTechnician}
                      disabled={isBusy}
                      onChange={(event) => handleVisibilityToggle(document, event.target.checked)}
                      className="h-4 w-4 cursor-pointer rounded border-line bg-bg text-amber focus:ring-amber disabled:cursor-not-allowed"
                    />
                    <span>Visible técnico</span>
                    {isUpdatingVisibility ? <SpinnerGap size={14} className="animate-spin" /> : null}
                  </label>

                  {isActionLoading ? (
                    <button
                      type="button"
                      disabled
                      className="flex h-9 w-9 min-h-[36px] min-w-[36px] flex-none items-center justify-center self-center rounded-md border border-line bg-bg text-content shadow-sm disabled:cursor-not-allowed disabled:opacity-70"
                      aria-label={isPreviewing ? 'Abriendo PDF' : 'Descargando documento'}
                    >
                      <SpinnerGap size={18} className="animate-spin" />
                    </button>
                  ) : (
                    <ActionMenu options={actionOptions} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DocumentPreviewModal
        isOpen={Boolean(previewDocument)}
        document={previewDocument}
        taskId={taskId}
        onClose={handleClosePreview}
      />

      {documentPendingDelete && (
        <Alerts
          title="Eliminar documento"
          onClose={handleCloseDeleteAlert}
          width="max-w-md"
        >
          <p className="text-sm text-content">
            {`¿Estás seguro de que quieres eliminar el documento "${documentPendingDelete.name}"?`}
          </p>

          <div className="flex justify-end gap-3 border-t border-line pt-6">
            <button
              type="button"
              onClick={handleCloseDeleteAlert}
              disabled={deletingId === documentPendingDelete.id}
              className="rounded-md bg-surface-2 px-4 py-2 text-content transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-70"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmDeleteDocument}
              disabled={deletingId === documentPendingDelete.id}
              className="inline-flex items-center gap-2 rounded-md bg-red-600 px-6 py-2 text-content transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {deletingId === documentPendingDelete.id ? (
                <>
                  <SpinnerGap size={16} className="animate-spin" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </button>
          </div>
        </Alerts>
      )}
    </div>
  );
}

export default DocumentsTab;
